import { Injectable, OnDestroy } from '@angular/core';
import { EmpresaUsuario } from '../../modulos/main/objetos/empresa';
import { EmpresaClases } from '../classes/empresasClases';
import { Usuario } from '../../modulos/main/objetos/usuario';
import { EmpresasService } from '../../modulos/main/services/empresas.service';
import { UsuarioUtilsService } from '../../modulos/punto-venta/services/utils/usuario-utils.service';
import { SimpleResponse } from '../objetos/simple-response';
import { firstValueFrom, map, Subject, takeUntil } from 'rxjs';
import { UsuariosService } from '../../modulos/main/services/usuarios.service';
import { Seleccionadas, SeleccionadasResponse } from '../objetos/seleccionada';
import { showToastAlert, SwalAlertTypes } from '../tools/swal-alerts';

/**
 * Servicio para obtener las empresas asignadas y seleccionadas del usuario, almacenadas de manera local
 */
@Injectable({
  providedIn: 'root',
})
export class UsuarioEmpresaService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private usuarioLocal: Usuario = {} as Usuario;

  constructor(
    private readonly empresasService: EmpresasService,
    private readonly usuarioUtils: UsuarioUtilsService,
    private readonly usuarioService: UsuariosService
  ) {
    this.usuarioLocal = this.obtenerUsuarioLocal();
  }

  obtenerUsuarioLocal(): Usuario {
    return JSON.parse(localStorage.getItem('user') || '{}') as Usuario;
  }

  obtenerEmpresasActualizadoLocal(): void {
    try {
      const asignadasPrevias = this.usuarioLocal?.empresasUsuario ?? [];
      this.usuarioLocal = this.obtenerUsuarioLocal();

      const asignadas = EmpresaClases.obtenerEmpresasAsignadasConId();
      const seleccionadas = EmpresaClases.obtenerEmpresasSeleccionadas();

      // Nunca se vacían las asignadas a partir de una lectura vacía: este objeto
      // se persiste después con `actualizarUsuarioLocal`, así que un [] pasajero
      // borraba las empresas del localStorage y el guard sacaba al usuario en la
      // siguiente recarga (sin forma de recuperarlas salvo re-login).
      this.usuarioLocal.empresasUsuario =
        asignadas.length > 0
          ? asignadas
          : (this.usuarioLocal.empresasUsuario?.length ? this.usuarioLocal.empresasUsuario : asignadasPrevias);
      this.usuarioLocal.empresasSeleccionadas = seleccionadas;
    } catch (error) {
      showToastAlert(
        'Error al obtener las empresas actualizadas local',
        SwalAlertTypes.ERROR
      );
    }
  }

  obtenerEmpresasActualizadoService(): void {
    this.usuarioLocal = this.obtenerUsuarioLocal();
    this.empresasService
      .obtenerEmpresasPorUsuario(this.usuarioUtils.usuario)
      .subscribe((result: SimpleResponse) => {
        if (result.payload) {
          this.usuarioLocal.empresasUsuario = [];
          this.usuarioLocal.empresasSeleccionadas = [];
          this.usuarioLocal.empresasUsuario = result.payload.asignadas;
          this.usuarioLocal.empresasSeleccionadas =
            result.payload.seleccionadas;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Obtiene las empresas asignadas del usuario almacenadas de manera local, filtradas por id
   * @returns EmpresaUsuario[] - Array de empresas asignadas
   */
  public obtenerEmpresasAsignadas(): EmpresaUsuario[] {
    this.obtenerEmpresasActualizadoLocal();
    return this.usuarioLocal.empresasUsuario || [];
  }

  /**
   * Obtener las empresas seleccionadas del usuario almacenadas de manera local
   * @returns EmpresaUsuario[] - Array de empresas seleccionadas del usuario, almacenadas de manera local
   */

  public obtenerEmpresasSeleccionadas = (): EmpresaUsuario[] => {
    this.obtenerEmpresasActualizadoLocal();
    return this.usuarioLocal.empresasSeleccionadas || [];
  };

  /**
   * Validar si el usuario tiene una empresa asignada, almacenadas de manera local
   * @returns boolean - TRUE si el usuario tiene una empresa asignada, FALSE si no tiene
   */
  public validarEmpresasAsignadas(): boolean {
    let regresa = true;
    this.obtenerEmpresasActualizadoLocal();
    if (this.usuarioLocal.empresasUsuario?.length === 0) {
      regresa = false;
    }
    return regresa;
  }

  /**
   * Validar si el usuario tiene una empresa asignada filtrando por tipo WMS como parametro opcional
   * @param WMS - boolean - (OPCIONAL) TRUE si es WMS, FALSE o sin parametro si no es WMS
   * @returns boolean - TRUE si el usuario tiene una empresa asignada de tipo WMS, FALSE si no tiene
   */
  public validarEmpresasAsignadasUsuarioTipo(WMS: boolean = false): boolean {
    let regresa = true;
    this.obtenerEmpresasActualizadoLocal();
    const EMPRESAS: EmpresaUsuario[] =
      this.usuarioLocal.empresasUsuario?.filter((empresa: EmpresaUsuario) => {
        return empresa.cedis === WMS;
      }) || ([] as EmpresaUsuario[]);
    if (EMPRESAS.length === 0) {
      regresa = false;
    }
    return regresa;
  }

  /**
   * Validar si el usuario tiene una empresa seleccionada, almacenadas de manera local, sin importar el tipo de empresa
   * @returns boolean - TRUE si el usuario tiene una empresa seleccionada, FALSE si no tiene
   */
  public validarEmpresasSeleccionada(): boolean {
    let regresa = true;

    if (this.usuarioLocal.empresasSeleccionadas?.length === 0) {
      regresa = false;
    }

    return regresa;
  }

  /**
   * Validar si el usuario tiene una empresa seleccionada, almacenadas de manera local, filtrando por el tipo de empresa
   * @param WMS - boolean - (OPCIONAL) TRUE si es WMS, FALSE o sin parametro si no es WMS
   * @returns boolean - TRUE si el usuario tiene una empresa seleccionada, FALSE si no tiene
   */
  public validarEmpresasSeleccionadaTipo(WMS: boolean = false): boolean {
    let regresa = true;

    const EMPRESAS: EmpresaUsuario[] =
      this.usuarioLocal.empresasSeleccionadas?.filter(
        (empresa: EmpresaUsuario) => {
          return empresa.cedis === WMS;
        }
      ) || ([] as EmpresaUsuario[]);

    if (EMPRESAS.length === 0) {
      regresa = false;
    }

    return regresa;
  }

  /**
   * Obtener las empresas asignadas del usuario almacenadas de manera local, filtradas por tipo WMS
   * @param WMS - boolean - (OPCIONAL) TRUE si es WMS, FALSE o sin parametro si no es WMS
   * @returns EmpresaUsuario[] - Array de empresas asignadas
   */
  public obtenerEmpresasAsignadasTipo(WMS: boolean = false): EmpresaUsuario[] {
    let empresas: EmpresaUsuario[] = [];
    empresas =
      this.obtenerEmpresasAsignadas().filter((empresa: EmpresaUsuario) => {
        return empresa.cedis === WMS;
      }) || ([] as EmpresaUsuario[]);
    return empresas;
  }

  public obtenerEmpresaAsignadasTipo(WMS: boolean = false): EmpresaUsuario {
    let empresa: EmpresaUsuario = {} as EmpresaUsuario;
    empresa =
      this.obtenerEmpresasAsignadas().find((empresa: EmpresaUsuario) => {
        return empresa.cedis === WMS;
      }) || ({} as EmpresaUsuario);
    return empresa;
  }

  /**
   * Obtener las empresas seleccionadas del usuario almacenadas de manera local, filtradas por tipo WMS
   * @param WMS - boolean - (OPCIONAL) TRUE si es WMS, FALSE o sin parametro si no es WMS
   * @returns EmpresaUsuario[] - Array de empresas seleccionadas
   */
  public obtenerEmpresasSeleccionadasTipo(
    WMS: boolean = false
  ): EmpresaUsuario[] {
    let empresas: EmpresaUsuario[] = [];
    empresas =
      this.obtenerEmpresasSeleccionadas().filter((empresa: EmpresaUsuario) => {
        return empresa.cedis === WMS;
      }) || ([] as EmpresaUsuario[]);
    return empresas;
  }

  /**
   *  Obtiene la primer empresa de la lista de  empresas seleccionadas del usuario almacenadas de manera local, filtradas por tipo WMS
   * @param WMS - boolean - (OPCIONAL) TRUE si es WMS, FALSE o sin parametro si no es WMS
   * @returns EmpresaUsuario - Empresa seleccionada o un objeto vacio si no hay empresas seleccionadas
   */
  public obtenerEmpresaSeleccionadaTipo(WMS: boolean = false): EmpresaUsuario {
    let empresas: EmpresaUsuario[] = [];
    empresas = this.obtenerEmpresasSeleccionadas();

    const empresaTipo = empresas.find((empresa: EmpresaUsuario) => {
        return empresa.cedis === WMS;
      }) || ({} as EmpresaUsuario);

    return empresaTipo;
  }

  public obtenerTotalEmpresasSeleccionadasTipo(WMS: boolean = false): number {
    const EMPRESAS =
      this.obtenerEmpresasSeleccionadas().filter((empresa: EmpresaUsuario) => {
        return empresa.cedis === WMS;
      }) || ([] as EmpresaUsuario[]);

    return EMPRESAS.length;
  }

  /**
   * Actualizar la empresa seleccionada del usuario en el servicio
   * @param empresa - EmpresaUsuario  local
   * @returns Promise<boolean> - Promise con el resultado de la actualización
   */
  public actualizarEmpresaSeleccionadaLocal(empresa: EmpresaUsuario): boolean {
    let regresa: boolean = false;
    let usuario: Usuario;
    let longitud: number = 0;
    let seleccionadas: EmpresaUsuario[] = [];

    if (empresa.empresaId > 0) {
      this.obtenerEmpresasActualizadoLocal();
      usuario = this.usuarioLocal;
      if (usuario.empresasSeleccionadas) {
        longitud = usuario.empresasSeleccionadas.length;

        seleccionadas.push(empresa);

        if (longitud >= 1) {
          for (const seleccionada of usuario.empresasSeleccionadas) {
            if (
              seleccionada.cedis !== empresa.cedis &&
              seleccionada.empresaId !== empresa.empresaId
            ) {
              seleccionadas.push(seleccionada);
            }
          }
        }
        usuario.empresasSeleccionadas = seleccionadas;
        this.actualizarUsuarioLocal(usuario);
        regresa = true;
      }
    }
    return regresa;
  }

  public actualizarEmpresaSeleccionadaLocalCambio(
    empresaAnterior: EmpresaUsuario,
    empresaCambio: EmpresaUsuario
  ): Promise<boolean> {
    let regresa: boolean = false;
    let usuario: Usuario;
    let longitud: number = 0;
    let seleccionadas: EmpresaUsuario[] = [];
    let actualizo: boolean = false;

    empresaCambio.empresaIdSeleccionada = empresaAnterior.empresaIdSeleccionada;

    this.obtenerEmpresasActualizadoLocal();
    usuario = this.usuarioLocal;
    if (usuario.empresasSeleccionadas) {
      longitud = usuario.empresasSeleccionadas.length;

      if (longitud < 1) {
        seleccionadas.push(empresaCambio);
      } else {
        for (const seleccionada of usuario.empresasSeleccionadas) {
          if (seleccionada.empresaId === empresaAnterior.empresaId) {
            actualizo = true;
            seleccionadas.push(empresaCambio);
          } else if (seleccionada.cedis !== empresaAnterior.cedis) {
            seleccionadas.push(seleccionada);
          }
        }
      }

      if (actualizo) {
        usuario.empresasSeleccionadas = seleccionadas;
        this.actualizarUsuarioLocal(usuario);
        regresa = true;
      }
    }
    return Promise.resolve(regresa);
  }

  /**
   * Actualizar la empresa seleccionada del usuario en el servicio
   * @param empresa - SeleccionadasResponse
   * @returns Promise<boolean> - Promise con el resultado de la actualización
   */
  public actualizarEmpresaSeleccionadaLocalResponse(
    empresas: SeleccionadasResponse
  ): boolean {
    let regresa: boolean = false;
    let longitud: number = 0;
    /* let seleccionadas: EmpresaUsuario[] = []; */
    this.obtenerEmpresasActualizadoLocal();

    if (this.usuarioLocal.empresasSeleccionadas) {
      this.usuarioLocal.empresasSeleccionadas?.splice(0);

      longitud = this.usuarioLocal.empresasSeleccionadas.length;

      if (longitud === 0 && empresas.empresa.length > 0) {
        this.usuarioLocal.empresasSeleccionadas = empresas.empresa;
      }

      /* for (const empresa of empresas.empresa) {
        for (const seleccionada of this.usuarioLocal.empresasSeleccionadas) {
          if (seleccionada.empresaId === empresa.empresaId) {
            seleccionadas.push(empresa);
          } else if (seleccionada.cedis !== empresa.cedis) {
            seleccionadas.push(seleccionada);
          }
        }
      }
      this.usuarioLocal.empresasSeleccionadas = seleccionadas; */
      this.actualizarUsuarioLocal(this.usuarioLocal);
      regresa = true;
    }
    return regresa;
  }

  /**
   * Actualizar la empresa seleccionada del usuario en el servicio
   * @param seleccionadas - Seleccionadas
   * @returns Promise<boolean> - Promise con el resultado de la actualización
   */
  public async actualizarEmpresaSeleccionadaService(
    seleccionadas: Seleccionadas
  ): Promise<SimpleResponse> {
    let regresa: SimpleResponse = {
      error: true,
      msg: 'Error al actualizar la empresa seleccionada',
      payload: null,
    };
    regresa = await firstValueFrom(
      this.usuarioService
        .actualizarEmpresasSeleccionadas(seleccionadas)
        .pipe(takeUntil(this.destroy$))
    );
    return Promise.resolve(regresa);
  }

  /**
   * Registrar la empresa seleccionada del usuario en el servicio
   * @param seleccionadas - Seleccionadas
   * @returns Promise<SimpleResponse> - Promise con el resultado de la actualización
   */
  public async registrarEmpresaSeleccionadaService(
    seleccionadas: Seleccionadas
  ): Promise<SimpleResponse> {
    let regresa: SimpleResponse = {
      error: true,
      msg: 'Ocurrió un error al registrar la empresa seleccionada, sin embargo, puede seguir operando el sistema.',
      payload: [] as EmpresaUsuario[],
    };
    regresa = await firstValueFrom(
      this.usuarioService
        .registrarEmpresasSeleccionadas(seleccionadas)
        .pipe(takeUntil(this.destroy$))
    );
    return Promise.resolve(regresa);
  }

  /**
   * Eliminar la empresa seleccionada del usuario en el servicio
   * @param empresa - EmpresaUsuario local
   * @param esWMS - boolean - (OPCIONAL) TRUE si es WMS, FALSE o sin parametro si no es WMS
   * @returns Promise<boolean> - Promise con el resultado de la eliminación
   */
  public eliminarEmpresaSeleccionadaLocal(empresa: EmpresaUsuario): boolean {
    let regresa: boolean = false;
    let seleccionadas: EmpresaUsuario[] = [];

    try {
      this.obtenerEmpresasActualizadoLocal();
      seleccionadas =
        this.usuarioLocal.empresasSeleccionadas?.filter((e: EmpresaUsuario) => {
          return e.empresaId !== empresa.empresaId;
        }) || ([] as EmpresaUsuario[]);
      if (seleccionadas.length > 0) {
        this.usuarioLocal.empresasSeleccionadas = seleccionadas;
        this.actualizarUsuarioLocal(this.usuarioLocal);
        regresa = true;
      } else {
        regresa = false;
      }
    } catch (error) {
      throw error;
    }
    return regresa;
  }

  /**
   * Eliminar la empresa seleccionada del usuario en el servicio
   * @param empresa - EmpresaUsuario local
   * @param esWMS - boolean - (OPCIONAL) TRUE si es WMS, FALSE o sin parametro si no es WMS
   * @returns Promise<boolean> - Promise con el resultado de la eliminación
   */
  public eliminarEmpresaSeleccionadaLocalTipo(
    empresa: EmpresaUsuario
  ): boolean {
    let regresa: boolean = false;
    let seleccionadas: EmpresaUsuario[] = [];

    try {
      this.obtenerEmpresasActualizadoLocal();
      seleccionadas =
        this.usuarioLocal.empresasSeleccionadas?.filter((e: EmpresaUsuario) => {
          return (
            (e.empresaId !== empresa.empresaId && e.cedis !== empresa.cedis) ||
            (e.empresaId === empresa.empresaId && e.cedis === empresa.cedis)
          );
        }) || ([] as EmpresaUsuario[]);
      if (seleccionadas.length > 0) {
        this.usuarioLocal.empresasSeleccionadas = seleccionadas;
        this.actualizarUsuarioLocal(this.usuarioLocal);
        regresa = true;
      } else {
        regresa = false;
      }
    } catch (error) {
      throw error;
    }
    return regresa;
  }

  /**
   * Eliminar la empresa seleccionada del usuario en el servicio
   * @param seleccionadas - Seleccionadas
   * @returns Promise<boolean> - Promise con el resultado de la eliminación
   */
  public eliminarEmpresaSeleccionadaService(
    seleccionadas: Seleccionadas
  ): Promise<boolean> {
    let regresa: boolean = false;
    this.usuarioService
      .eliminarEmpresasSeleccionadas(seleccionadas)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: SimpleResponse) => {
        regresa = result.error;
        if (regresa) {
          throw new Error(result.msg);
        }
      });
    return Promise.resolve(regresa);
  }

  /**
   * Actualizar el usuario local
   * @param usuario - Usuario
   * @returns void
   */
  private actualizarUsuarioLocal(usuario: Usuario): void {
    // Salvaguarda: si el objeto a persistir perdió las empresas asignadas pero en
    // localStorage sí había, se conservan las guardadas. Sin esto, cualquier
    // guardado con `empresasUsuario: []` dejaba al usuario "sin empresas" de
    // forma permanente y los guards lo expulsaban al recargar.
    if (!usuario?.empresasUsuario?.length) {
      const guardadas = this.obtenerUsuarioLocal()?.empresasUsuario ?? [];
      if (guardadas.length > 0) {
        usuario = { ...usuario, empresasUsuario: guardadas } as Usuario;
      }
    }

    localStorage.removeItem('user');
    localStorage.setItem('user', JSON.stringify(usuario));
  }

  /**
   * Obtener el nombre de la empresa seleccionada
   * @param esWms - boolean - (OPCIONAL) TRUE si es WMS, FALSE o sin parametro si no es WMS
   * @returns string - Nombre de la empresa seleccionada
   */
  public obtenerNombreEmpresasSeleccionadas(esWms: boolean): string {
    let nombre: string = '';
    nombre = this.obtenerEmpresaSeleccionadaTipo(esWms).nombreComercial;
    return nombre;
  }

  public actualizarEmpresaAsignadaLocal(empresas: EmpresaUsuario[]): void {
    this.obtenerEmpresasActualizadoLocal();

    if (empresas.length > 0) {
      const empresasSeleccionadasResp = this.obtenerEmpresasSeleccionadas();

      if (empresasSeleccionadasResp.length > 0) {
        // Join entre empresasSeleccionadasResp y empresas para mantener solo las que siguen asignadas
        const empresasSeleccionadasActualizadas = empresasSeleccionadasResp.filter(seleccionada =>
          empresas.some(asignada =>
            asignada.id === seleccionada.id
          )
        );

        this.usuarioLocal.empresasSeleccionadas = empresasSeleccionadasActualizadas ?? [];
      } else {
        this.usuarioLocal.empresasSeleccionadas = [];
      }

      this.usuarioLocal.empresasUsuario = empresas;
      this.actualizarUsuarioLocal(this.usuarioLocal);
    }
  }

  /**
   * Obtener la empresa relacionada según el tipo y la empresa seleccionada
   * @param esCedis - boolean - TRUE si es CEDIS, FALSE si no es CEDIS
   * @param empresaSeleccionadaId - number - ID de la empresa seleccionada
   * @param suministro - boolean - (OPCIONAL) TRUE para que, cuando no es CEDIS, el backend
   * desambigüe el CEDIS "padre" usando la bandera `suministros` de RelacionSucursales
   * (ver docs/relaciones-sucursales.md) en vez de devolver el mismo resultado para toda tienda
   * @returns Observable<EmpresaUsuario | EmpresaUsuario[]>
   */

  public obtenerEmpresaRelacionada(
    esCedis: boolean,
    empresaSeleccionadaId: number,
    suministro: boolean = false
  ) {
    return this.empresasService
      .obtenerRelacionSucursal(esCedis, empresaSeleccionadaId, suministro)
      .pipe(
        takeUntil(this.destroy$),
        map((result: SimpleResponse) => {
          if (result.error) {
            throw new Error(result.msg);
          }
          const empresas = result.payload as EmpresaUsuario[] | EmpresaUsuario;
          this.usuarioLocal.empresasRelacionadas = empresas;
          this.actualizarUsuarioLocal(this.usuarioLocal);
          return empresas;
        })
      );
  }
}
